import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { CheckCircle2 } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const HeroSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Web3Forms API - sempre funciona!
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '1ef78500-ebfa-4692-a1a0-e5ead7a8a000',
          subject: 'Novo contacto do site - ' + formData.name,
          from_name: 'Bruno Rodrigues Fit Website',
          name: formData.name,
          email: formData.email,
          phone: `+${formData.phone}`,
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowSuccess(true);
        toast({
          title: 'Pedido enviado!',
          description: 'Entraremos em contacto em breve.',
        });
        setFormData({ name: '', email: '', phone: '' });
        // Esconder mensagem após 5 segundos
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar. Tenta novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToPlans = () => {
    const plansSection = document.getElementById('planos');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-[#F8F9FA] to-[#F8F9FA] min-h-screen overflow-visible z-[200]">
      {/* Header/Navbar */}
      <nav className="absolute top-0 left-0 right-0 z-50 px-6 md:px-12 py-4 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/images/logo.webp" 
            alt="Bruno Rodrigues Advanced Trainer" 
            className="h-20 md:h-24 w-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          />
        </div>
        <a 
          href="https://wa.me/351910126339?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20acompanhamento."
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 inline-block"
        >
          Fala com o Bruno
        </a>
      </nav>

      {/* Mobile Layout - Image on top, content below */}
      <div className="md:hidden relative min-h-screen overflow-hidden bg-[#F8F9FA]">
        {/* Top section with image - starts below navbar */}
        <div className="relative w-full pt-28">
          <img
            src="https://customer-assets.emergentagent.com/job_fitness-coach-hub-12/artifacts/p0pj71gl_bruno%20section%201%20color.jpg"
            alt="Bruno Rodrigues - Personal Trainer"
            className="w-full h-auto object-contain"
          />
          {/* Fade to bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#F8F9FA] to-transparent"></div>
        </div>

        {/* Content section - less overlap to show PT face */}
        <div className="relative z-10 px-6 pb-12 -mt-16 bg-transparent">
          <div className="bg-transparent pt-6 px-1 space-y-6">
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">Bem vindo(a) à equipa Bruno Rodrigues</p>
              <h1 className="text-3xl font-bold text-black leading-tight">
                A tua <span className="text-[#fff301]">perda de peso com saúde</span> começa aqui!
              </h1>
              <p className="text-base text-gray-700 leading-relaxed">
                O acompanhamento online que te ensina a perder peso de forma sustentável, com um plano treino e nutrição, personalizado para ti, priorizando a tua saúde acima de tudo, sem restrições desnecessárias.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-4">
              <Button 
                onClick={scrollToPlans}
                className="w-full bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 shadow-lg h-auto"
              >
                Começar já!
              </Button>
              <Button 
                asChild
                variant="outline"
                className="w-full border-2 border-black bg-white/90 hover:bg-black hover:text-white text-black font-semibold px-8 py-6 rounded-full text-lg transition-all duration-300 h-auto"
              >
                <a 
                  href="https://wa.me/351910126339?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20acompanhamento."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Fala connosco
                </a>
              </Button>
            </div>

            {/* Contact Form */}
            <div className="bg-white/95 backdrop-blur-sm p-6 rounded-3xl shadow-xl relative z-[100]" id="contactos">
              {/* Success Message */}
              {showSuccess && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 rounded-xl flex items-center gap-3 animate-pulse">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-bold text-green-800">Mensagem enviada!</p>
                    <p className="text-sm text-green-700">Entraremos em contacto em breve.</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-gray-900">
                    Nome
                  </Label>
                  <Input
                    id="name"
                    placeholder="Insere o teu nome"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-white border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-900">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Insere o teu email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2 bg-white border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-900">
                    Telefone
                  </Label>
                  <div className="mt-2">
                    <PhoneInput
                      country={'pt'}
                      value={formData.phone}
                      onChange={(phone) => setFormData({ ...formData, phone })}
                      inputProps={{
                        name: 'phone',
                        required: true,
                        id: 'phone'
                      }}
                      containerClass="phone-input-container"
                      inputClass="phone-input-field"
                      buttonClass="phone-input-button"
                      dropdownClass="phone-input-dropdown"
                      searchClass="phone-input-search"
                      enableSearch={true}
                      searchPlaceholder="Pesquisar país..."
                      preferredCountries={['pt', 'br', 'es', 'fr', 'gb', 'de', 'it', 'ch', 'us']}
                      localization={{
                        pt: 'Portugal',
                        br: 'Brasil',
                        es: 'Espanha',
                        fr: 'França',
                        gb: 'Reino Unido',
                        de: 'Alemanha',
                        it: 'Itália',
                        ch: 'Suíça',
                        us: 'Estados Unidos'
                      }}
                    />
                  </div>
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  {isSubmitting ? 'A enviar...' : 'Enviar'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Image as background with fade */}
      <div className="hidden md:block relative min-h-screen">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_fitness-coach-hub-12/artifacts/p0pj71gl_bruno%20section%201%20color.jpg"
            alt="Bruno Rodrigues - Personal Trainer"
            className="w-full h-full object-cover object-center"
          />
          {/* Gradient overlay - strong white on left, clear on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-white from-0% via-white/90 via-45% to-transparent to-70%"></div>
        </div>

        {/* Content on top */}
        <div className="relative z-10 container mx-auto px-6 md:px-12 pt-32 pb-20">
          <div className="max-w-2xl">
            {/* Left Column - Text and Form */}
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700">Bem vindo(a) à equipa Bruno Rodrigues</p>
                <h1 className="text-4xl md:text-6xl font-bold text-black leading-tight">
                  A tua <span className="text-[#fff301]">perda de peso com saúde</span> começa aqui!
                </h1>
                <p className="text-lg text-gray-700 leading-relaxed">
                  O acompanhamento online que te ensina a perder peso de forma sustentável, com um plano treino e nutrição, personalizado para ti, priorizando a tua saúde acima de tudo, sem restrições desnecessárias.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={scrollToPlans}
                  className="bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 shadow-lg h-auto"
                >
                  Começar já!
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-2 border-black bg-white/90 hover:bg-black hover:text-white text-black font-semibold px-8 py-6 rounded-full text-lg transition-all duration-300 h-auto"
                >
                  <a 
                    href="https://wa.me/351910126339?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20acompanhamento."
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Fala connosco
                  </a>
                </Button>
              </div>

              {/* Contact Form */}
              <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-xl relative z-[100]" id="contactos-desktop">
                {/* Success Message */}
                {showSuccess && (
                  <div className="mb-4 p-4 bg-green-100 border border-green-400 rounded-xl flex items-center gap-3 animate-pulse">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-bold text-green-800">Mensagem enviada!</p>
                      <p className="text-sm text-green-700">Entraremos em contacto em breve.</p>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name-desktop" className="text-sm font-medium text-gray-900">
                      Nome
                    </Label>
                    <Input
                      id="name-desktop"
                      placeholder="Insere o teu nome"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-2 bg-white border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email-desktop" className="text-sm font-medium text-gray-900">
                      Email
                    </Label>
                    <Input
                      id="email-desktop"
                      type="email"
                      placeholder="Insere o teu email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-2 bg-white border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone-desktop" className="text-sm font-medium text-gray-900">
                      Telefone
                    </Label>
                    <div className="mt-2">
                      <PhoneInput
                        country={'pt'}
                        value={formData.phone}
                        onChange={(phone) => setFormData({ ...formData, phone })}
                        inputProps={{
                          name: 'phone-desktop',
                          required: true,
                          id: 'phone-desktop'
                        }}
                        containerClass="phone-input-container"
                        inputClass="phone-input-field"
                        buttonClass="phone-input-button"
                        dropdownClass="phone-input-dropdown"
                        searchClass="phone-input-search"
                        enableSearch={true}
                        searchPlaceholder="Pesquisar país..."
                        preferredCountries={['pt', 'br', 'es', 'fr', 'gb', 'de', 'it', 'ch', 'us']}
                        localization={{
                          pt: 'Portugal',
                          br: 'Brasil',
                          es: 'Espanha',
                          fr: 'França',
                          gb: 'Reino Unido',
                          de: 'Alemanha',
                          it: 'Itália',
                          ch: 'Suíça',
                          us: 'Estados Unidos'
                        }}
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#fff301] hover:bg-[#e6dd01] text-black font-semibold py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50"
                  >
                    {isSubmitting ? 'A enviar...' : 'Enviar'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;